import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import format from "date-fns/format";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
} from "@mui/material";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useAttendanceData from "layouts/tables/data/attendanceTableData";

function Attendance() {
  const {
    columns,
    rows,
    isLoading,
    error,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    showAbsentMembers,
    handleShowAbsent,
    selectedDate,
    handleDateChange,
    fetchAttendanceData,
    // Manual attendance properties
    manualAttendanceDialog,
    setManualAttendanceDialog,
    selectedMember,
    setSelectedMember,
    searchQuery,
    setSearchQuery,
    filteredMembers,
    takeManualAttendance,
  } = useAttendanceData();

  const [dateRange, setDateRange] = React.useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const handleStartDateChange = (date) => {
    const newDateRange = { ...dateRange, startDate: date };
    setDateRange(newDateRange);
    fetchAttendanceData(date, dateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = { ...dateRange, endDate: date };
    setDateRange(newDateRange);
    fetchAttendanceData(dateRange.startDate, date);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateRange({ startDate: today, endDate: today });
    fetchAttendanceData(today, today);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading Attendances...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      Church Attendance - {formatDisplayDate(dateRange.startDate)}
                      {dateRange.startDate.getTime() !== dateRange.endDate.getTime() &&
                        ` to ${formatDisplayDate(dateRange.endDate)}`}
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="From Date"
                          value={dateRange.startDate}
                          onChange={handleStartDateChange}
                          maxDate={dateRange.endDate}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                                width: 150,
                              }}
                            />
                          )}
                        />
                        <DatePicker
                          label="To Date"
                          value={dateRange.endDate}
                          onChange={handleEndDateChange}
                          minDate={dateRange.startDate}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                                width: 150,
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      <MDButton variant="outlined" color="white" onClick={handleTodayClick}>
                        Today
                      </MDButton>
                      <MDButton
                        variant={showOnlyUnchecked ? "contained" : "outlined"}
                        color="white"
                        onClick={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
                      >
                        {showOnlyUnchecked ? "Show All" : "Show Only Unchecked"}
                      </MDButton>
                      <MDButton
                        variant={showAbsentMembers ? "contained" : "outlined"}
                        color="white"
                        onClick={handleShowAbsent}
                      >
                        {showAbsentMembers ? "Hide Absent" : "Show Absent"}
                      </MDButton>
                      {/* Add Manual Attendance Button */}
                      <MDButton
                        variant="contained"
                        color="secondary"
                        onClick={() => setManualAttendanceDialog(true)}
                      >
                        <Icon>person_add</Icon>&nbsp;Manual Attendance
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search Members..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Manual Attendance Dialog */}
      <Dialog
        open={manualAttendanceDialog}
        onClose={() => {
          setManualAttendanceDialog(false);
          setSelectedMember(null);
          setSearchQuery("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Manual Attendance</DialogTitle>
        <DialogContent>
          <MDBox mt={2}>
            <Autocomplete
              options={filteredMembers}
              getOptionLabel={(option) => `${option.first_name} ${option.surname}`}
              value={selectedMember}
              onChange={(event, newValue) => setSelectedMember(newValue)}
              inputValue={searchQuery}
              onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} label="Search Member" variant="outlined" fullWidth />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <MDBox display="flex" alignItems="center">
                    <MDAvatar
                      name={`${option.first_name} ${option.surname}`}
                      size="sm"
                      sx={{ mr: 1 }}
                    />
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium">
                        {option.first_name} {option.surname}
                      </MDTypography>
                      <MDTypography variant="caption" display="block">
                        {option.phone} • {option.email}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </li>
              )}
            />
          </MDBox>
          {selectedMember && (
            <MDBox mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
              <MDTypography variant="body2">
                Selected:{" "}
                <strong>
                  {selectedMember.first_name} {selectedMember.surname}
                </strong>
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setManualAttendanceDialog(false);
              setSelectedMember(null);
              setSearchQuery("");
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={takeManualAttendance}
            disabled={!selectedMember}
            variant="gradient"
            color="info"
          >
            Mark Present
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Attendance;
